import _ from 'lodash';

import { INPUT, INPUT_TYPES } from '$gbusiness/enums';
import InputRowModel from '$gbusiness/models/inputRow';
import { input } from '$gbusiness/helpers';
import { addYears } from 'date-fns/esm';
import { DATE_FILTER_ITEMS } from '$fcomponents/filters/filterForm';
import { SERIAL_NUMBER_INPUT } from '../serialsScreen.tsx/serialForm';

export const BATCH_FILTER_FORM = (items, vendors, onChangeItem, isExpiration = false) => [
  {
    items: [...DATE_FILTER_ITEMS],
  },
  ...(!isExpiration
    ? [
        {
          items: [
            {
              input: INPUT.AUTOCOMPLETE,
              label: 'SCREEN.STORES.COLS.ITEM',
              disableFastField: true,
              gridSize: 5,
              value: 'item',
              config: {
                optionProp: {
                  label: 'name',
                  labelText: (o) =>
                    `${o.sku ? `(${o.sku})` : ''} ${o.name || ''} ${o.barcode ? `(${o.barcode})` : ''}`,
                  value: 'id',
                },
                data: items,
              },
              onChange: onChangeItem,
            },
            {
              input: INPUT.SELECT,
              label: 'SCREEN.BATCH.COLS.VENDOR',
              value: 'vendorId',
              options: vendors.map((v) => ({
                labelText: v.name,
                value: v.id,
              })),
              gridSize: 3,
            },
          ],
        },
      ]
    : []),
];

export const BATCH_FORM_GENERAL = ({ vendors, warehouses, settings, onChangeVendor, disabled = false }) => [
  {
    items: [
      {
        label: 'SCREEN.BATCH.COLS.RECEIVENO',
        value: 'receiveNo',
        input: INPUT.TEXT,
        disabled: true,
        gridSize: 3,
        validation: [input.requiredValidation],
      },
      ...(settings?.inventory && warehouses.length > 0
        ? [
            {
              label: 'SCREEN.BATCH.COLS.WAREHOUSE',
              value: 'warehouseId',
              input: INPUT.SELECT,
              disabled,
              options: warehouses.map((v) => ({
                labelText: v.name,
                value: v.id,
              })),
              gridSize: 4,
              validation: [input.requiredValidation],
            },
          ]
        : []),
      {
        label: 'SCREEN.BATCH.COLS.VENDOR',
        value: 'vendor',
        disableFastField: true,
        input: INPUT.AUTOCOMPLETE,
        disabled,
        config: {
          optionProp: {
            label: 'name',
            labelText: (o) => {
              return o.name;
            },
            value: 'id',
          },
          data: vendors,
        },
        gridSize: settings?.inventory ? 5 : 9,
        onChange: ({ value }) => {
          onChangeVendor(value.id);
        },
      },
      {
        label: 'SCREEN.BATCH.COLS.RECEIVE_DATE',
        value: 'receiveDate',
        input: INPUT.DATE,
        disabled,
        maxDate: addYears(new Date(), 1),
        gridSize: 4,
        validation: [input.requiredValidation],
      },
      {
        label: 'SCREEN.BATCH.COLS.SELL_BY',
        value: 'sellByDate',
        input: INPUT.DATE,
        disabled,
        maxDate: addYears(new Date(), 20),
        gridSize: 4,
      },
      {
        label: 'SCREEN.BATCH.COLS.EXPIRY',
        value: 'expirationDate',
        input: INPUT.DATE,
        disabled,
        maxDate: addYears(new Date(), 20),
        gridSize: 4,
      },
      {
        label: 'SCREEN.BATCH.COLS.LOTNO',
        value: 'lotNo',
        disabled,
        input: INPUT.TEXT,
        gridSize: 3,
      },
      {
        label: 'SCREEN.BATCH.COLS.BINNO',
        value: 'binNo',
        disabled,
        input: INPUT.TEXT,
        gridSize: 3,
      },
      {
        label: 'SCREEN.BATCH.COLS.QUANTITY',
        value: 'quantity',
        input: INPUT.NUMBER,
        FastField: true,
        disabled,
        gridSize: 3,
        validation: [input.requiredValidation],
      },
      {
        ...INPUT_TYPES.AMOUNT,
        label: 'SCREEN.BATCH.COLS.COST',
        value: 'cost',
        disabled,
        gridSize: 3,
      },
    ],
  },
];

export const BATCH_SCAN_FORM = ({ scanMode, onScanValue }) => {
  let debounceScan = _.debounce((formik, value) => {
    onScanValue(formik, value);
  }, 500);

  return [
    {
      items: [
        {
          label: '',
          value: 'scanText',
          rows: 6,
          isHidden: () => !scanMode,
          className: 'scan-focus',
          input: INPUT.TEXT,
          placeholder: 'Focus Here and Scan Barcode',
          size: 'large',
          gridSize: 12,
          onChange: ({ formik, value }) => {
            debounceScan(formik, value);
          },
        },
      ],
    },
  ];
};

export const BATCH_FORM = ({
  vendors,
  items = null,
  warehouses,
  scanMode,
  settings,
  isEdit,
  onChangeMode,
  onChangeVendor,
  disabled = false,
}): Array<InputRowModel> => {
  return [
    {
      items: [
        ...(items
          ? [
              {
                input: INPUT.SWITCH,
                label: 'SCREEN.BATCH.COLS.ADD_SCAN',
                disabled,
                isHidden: () => isEdit,
                gridSize: 6,
                value: 'scanMode',
                onChange: ({ checked }) => {
                  onChangeMode(checked);
                },
              },
              {
                input: INPUT.AUTOCOMPLETE,
                label: 'SCREEN.STORES.COLS.ITEM',
                disableFastField: true,
                disabled: scanMode,
                isHidden: () => scanMode,
                gridSize: 6,
                value: 'item',
                config: {
                  optionProp: {
                    label: 'name',
                    labelText: (o) =>
                      `${o.sku ? `(${o.sku})` : ''} ${o.name || ''} ${o.barcode ? `(${o.barcode})` : ''}`,
                    value: 'id',
                  },
                  data: items,
                },
              },
            ]
          : [
              {
                input: INPUT.TEXT,
                label: 'SCREEN.STORES.COLS.ITEM',
                disabled: true,
                gridSize: 12,
                value: 'itemName',
              },
            ]),
      ],
    },
    ...BATCH_FORM_GENERAL({ vendors, warehouses, onChangeVendor, settings, disabled }),
  ];
};

export const BATCH_MEMO_FORM = ({ disabled }) => [
  {
    items: [
      {
        label: 'SCREEN.BATCH.COLS.MEMO',
        value: 'memo',
        input: INPUT.TEXTAREA,
        disabled,
        rows: 6,
        gridSize: 12,
      },
    ],
  },
];

export const BATCH_SERIALS_FORM = ({ isEdit = false, settings }) => {
  if (settings?.serial && !isEdit) {
    return [
      {
        items: SERIAL_NUMBER_INPUT(),
      },
    ];
  }
  return [];
};
